import { z } from "zod";
import { gql, useMutation } from "@apollo/client";
import { CREATE_SADDLE_FAVORITE, DELETE_SADDLE_FAVORITE } from "apollo/mutations/saddleFavorite";
import ModalStore from "components/Modal/ModalStore";
import { useCartStore } from "stores/cart/useCartStore";
import { useModalStore, Modal as ModalKey } from "stores/modal/useModalStore";

// The base styles in components/ui/single-select were tuned for a ~52px-tall
// control (placeholder paddingTop / indicator marginBottom offsets). The 40px
// selects on this page need real vertical centering instead of those offsets.
export const saddleSelectStyles = {
  valueContainer: (base) => ({
    ...base,
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
    height: "100%",
    padding: "0 4px 0 12px",
    overflow: "hidden",
    whiteSpace: "nowrap",
    cursor: "pointer",
  }),
  placeholder: (base) => ({ ...base, margin: 0 }),
  singleValue: (base) => ({ ...base, margin: 0 }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: "100%" }),
  clearIndicator: (base) => ({ ...base, cursor: "pointer", padding: "0 2px" }),
  dropdownIndicator: (base) => ({
    ...base,
    cursor: "pointer",
    padding: "0 10px 0 2px",
  }),
};

export const saddleSchema = z.object({
  // Dropdown sets size to a Number (parseFloat of VARIANT.size). Accept either,
  // and require a positive numeric value so the empty default ("") fails validation.
  size: z
    .union([z.string(), z.number()])
    .refine(
      (v) => {
        const n = typeof v === "number" ? v : parseFloat(v);
        return Number.isFinite(n) && n > 0;
      },
      { message: "Please select a seat size" }
    ),
  color: z.string().nullable().optional(),
  selectedHorse: z.any().nullable(),
  selectedHorseStatus: z.any().nullable(),
  updatedSaddle: z.any().nullable(),
  favouriteLoading: z.boolean(),
  currentIndex: z.number(),
});

export const useSaddleMutations = (setValue, selectedHorse) => {
  const [createSaddleFavourite] = useMutation(gql(CREATE_SADDLE_FAVORITE), {
    refetchQueries: ["listSaddleFavorites"],
  });
  
  const [deleteSaddleFavourite] = useMutation(gql(DELETE_SADDLE_FAVORITE), {
    refetchQueries: ["listSaddleFavorites"],
  });

  const handleFavourite = async (item, favouriteList) => {
    setValue("favouriteLoading", true);
    try {
      if (favouriteList?.includes(item?.sk)) {
        await deleteSaddleFavourite({
          variables: { saddleId: item?.sk }
        });
      } else {
        await createSaddleFavourite({
          variables: {
            input: {
              saddleId: item?.sk,
              horseId: selectedHorse?.value,
              score: item?.score,
              brandName: item?.brandName,
            },
          },
        });
      }
    } catch (error) {
      ModalStore.openSimpleError({
        icon: undefined,
        title: "Error",
        subtitle: error?.message,
        shouldCloseOnOverlayClick: false
      });
    } finally {
      setValue("favouriteLoading", false);
    }
  };

  return { handleFavourite };
};

export const formatPrice = (price) => 
  price?.toFixed(0)?.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

export const formatBrandName = (brandName) => 
  brandName?.toLowerCase().replace("cactussaddlery", "Cactus");

export const formatProductTitle = (productTitle) => 
  productTitle?.includes("Cactus") 
    ? productTitle.replace("Cactus", "")?.trim() 
    : productTitle || "Rancher";

export const formatDescription = (description) => 
  description
    ?.replace(/<meta[^>]*>/g, "")
    .replace(/<p[^>]*>/g, '<p class="mb-4">')
    .replace(/<ul[^>]*>/g, '<ul class="list-disc pl-6 space-y-2">')
    .replace(/<li[^>]*>/g, '<li class="text-gray-700 text-sm">')
    .replace(/[\u2028\u2029]/g, "")
    .replace(/FEATURES/g, "<strong>FEATURES</strong>")
    .replace(
      /\b(Tree|Tree Warranty|Bars|Gullet|Gullet Height|Gullet Width|Cantle Height|Weight|In-Skirt Rigging|6" Flank Cinch|Hermann Oak Leather|Custom Hardware|Genuine Wool Skin Lined):/g,
      "<strong>$1:</strong>"
    )
    .replace(/Gullet Width: 6 ½"/g, "<strong>Gullet Width: 6 ½\"</strong>");

export const useCartActions = () => {
  const handleAddToCart = useCartStore((state) => state.handleAddToCart);
  const onBuyNowHandler = useCartStore((action) => action.proceedBtnHandler);
  const addToCartLoading = useCartStore((state) => state.loadingState?.addToCartLoading);
  const checkoutLoading = useCartStore((state) => state.loadingState?.checkoutLoading);
  const open = useModalStore(s => s.open);

  return { handleAddToCart, onBuyNowHandler, addToCartLoading, checkoutLoading, open, ModalKey };
};